import { useT } from "@transifex/react";
import { useEffect, useRef } from "react";
import { ReactComponent as IconLogin } from "../../assets/icons/lock-cropped.svg";
import monsterImg from "../../assets/img/monster-cookie.webp";
import { ButtonColorVariant, ButtonVariant, AreaButton } from "../../complex/teacherArea/AreaButton";
import { FormattedTextWithLink } from "../../complex/teacherArea/FormattedTextWithLink";
import { Headline } from "../../complex/teacherArea/Headline";
import { cp } from "../../config";
import { COOKIE_ACTIONS, CookieActionsType } from "../../enums/cookies";
import useBreakpoints, { Breakpoint } from "../../hooks/useBreakpoints";
import { useLogger } from "../../hooks/useLogger";
import { CookieService } from "../../services/cookie.service";

export const CookieConsentModal = () => {
    const t = useT();

    const { getStyleBasedOnBreakpoint } = useBreakpoints();
    const { logEvent } = useLogger();

    const modalRef = useRef<HTMLDialogElement>(null);

    useEffect(() => {
        CookieService.getInstance().registerConsentModal(modalRef.current);
    }, []);

    const t_cookieConsent = t("Cookie Consent", { _tags: "TeacherArea" });
    const t_headline = t("Which cookie type are you?", { _tags: "TeacherArea" });
    const t_description = t(
        'We use cookies and similar technologies to analyze the use of our website, optimize our content, and measure marketing efforts. Personal data may be processed and used to personalize ads. You can find detailed information about this in our <a>Privacy Policy</a>. You can reject all non-essential cookies or make your selections via "Configure Cookies". You can change your mind at any time.',
        { _tags: "TeacherArea" }
    );
    const t_login = t("Login", { _tags: "TeacherArea" });
    const t_decline = t("Decline", { _tags: "TeacherArea" });
    const t_configure = t("Configure Cookies", { _tags: "TeacherArea" });
    const t_acceptAll = t("Accept All", { _tags: "TeacherArea" });
    const t_imprint = t("Imprint", { _tags: "TeacherArea" });
    const t_alt_monsterCookie = t("Monster Cookie", { _tags: "TeacherArea" });

    const URLS = {
        LOGIN: `${cp.cfg.REACT_APP_P6_BASE_WEBSITE}classic/service/user/login.html?nogdpr=true`,
        IMPRINT: `${cp.cfg.REACT_APP_P6_BASE_WEBSITE}v3/impressum/?nogdpr=true`,
    } as const;

    useEffect(() => {
        const checkAndShowModal = () => {
            const service = CookieService.getInstance();
            service.checkAndShowConsentModal();
        };

        if (window.cookieService) {
            checkAndShowModal();
        } else {
            document.addEventListener("cookies:serviceReady", checkAndShowModal);
            return () => document.removeEventListener("cookies:serviceReady", checkAndShowModal);
        }
    }, []);

    const handleCookieAction = (action: CookieActionsType) => {
        const actionButtons = document.querySelectorAll("button[data-cookie-action]");

        switch (action) {
            case COOKIE_ACTIONS.DECLINE:
                document.dispatchEvent(
                    new CustomEvent("cookies:saveDeclined", {
                        detail: { actionButtons },
                    })
                );
                break;
            case COOKIE_ACTIONS.CONFIGURE:
                document.dispatchEvent(new CustomEvent("cookies:showPrefs"));
                break;
            case COOKIE_ACTIONS.ACCEPT_ALL:
                document.dispatchEvent(
                    new CustomEvent("cookies:saveAll", {
                        detail: { actionButtons },
                    })
                );
                break;
        }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === "Escape") {
            event.preventDefault();
            event.stopPropagation();
        }
    };

    useEffect(() => {
        const modalElement = document.getElementById("cookie-consent-modal");

        if (modalElement) {
            modalElement.addEventListener("keydown", handleKeyDown);
            return () => {
                modalElement.removeEventListener("keydown", handleKeyDown);
            };
        }
    }, []);

    return (
        <dialog
            ref={modalRef}
            id="cookie-consent-modal"
            className="w-11/12 max-w-4xl self-center justify-self-center rounded-lg border-none bg-white shadow-xl"
        >
            <article
                className="p-s gap-m flex flex-col"
                aria-label={`${t_cookieConsent}`}
            >
                <section className="flow-root">
                    <div className="float-right">
                        <div className="flex h-[160px] w-[160px] items-start justify-end">
                            <img
                                src={monsterImg}
                                alt={t_alt_monsterCookie}
                                className="bg-stone-300 h-[150px] w-[150px] rounded-circle object-cover"
                            />
                        </div>
                    </div>

                    <Headline
                        level={2}
                        className="text-stone-1000 text-xxl py-s"
                    >
                        {t_headline}
                    </Headline>

                    <FormattedTextWithLink
                        rawText={t_description}
                        href={`${cp.cfg.REACT_APP_P6_BASE_WEBSITE}v3/datenschutz-hub/?nogdpr=true`}
                        class="text-m text-stone-1000"
                        textHighlightClass="text-forest-green-800 hover:underline"
                        openInNewTab={true}
                    />
                </section>

                <div className="gap-m flex flex-col">
                    <div
                        className={`gap-xs flex justify-between ${getStyleBasedOnBreakpoint(
                            Breakpoint.SM,
                            "flex-row",
                            "flex-col"
                        )}`}
                    >
                        <AreaButton
                            buttonId="cookie-login-btn"
                            variant={ButtonVariant.SECONDARY}
                            colorVariant={ButtonColorVariant.LUMINA}
                            role="link"
                            href={URLS.LOGIN}
                            onClick={() => {
                                logEvent("Login Button Clicked", { source: "cookie-consent" });
                            }}
                        >
                            {t_login}
                            <IconLogin
                                className="ml-xs w-[4]"
                                fill="currentColor"
                            />
                        </AreaButton>

                        <div
                            id="cookie-action-container"
                            className={`gap-xs flex whitespace-nowrap ${getStyleBasedOnBreakpoint(
                                Breakpoint.SM,
                                "flex-row",
                                "flex-col"
                            )}`}
                        >
                            <AreaButton
                                buttonId="cookie-decline-btn"
                                variant={ButtonVariant.SECONDARY}
                                colorVariant={ButtonColorVariant.LUMINA}
                                role="button"
                                data-cookie-action={COOKIE_ACTIONS.DECLINE}
                                onClick={() => handleCookieAction(COOKIE_ACTIONS.DECLINE)}
                            >
                                {t_decline}
                            </AreaButton>

                            <AreaButton
                                buttonId="cookie-configure-btn"
                                variant={ButtonVariant.SECONDARY}
                                colorVariant={ButtonColorVariant.LUMINA}
                                role="button"
                                data-cookie-action={COOKIE_ACTIONS.CONFIGURE}
                                onClick={() => handleCookieAction(COOKIE_ACTIONS.CONFIGURE)}
                            >
                                {t_configure}
                            </AreaButton>

                            <AreaButton
                                buttonId="cookie-accept-all-btn"
                                variant={ButtonVariant.PRIMARY}
                                colorVariant={ButtonColorVariant.LUMINA}
                                role="button"
                                data-cookie-action={COOKIE_ACTIONS.ACCEPT_ALL}
                                onClick={() => handleCookieAction(COOKIE_ACTIONS.ACCEPT_ALL)}
                            >
                                {t_acceptAll}
                            </AreaButton>
                        </div>
                    </div>

                    <a
                        href={URLS.IMPRINT}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-forest-green-800 text-l self-start hover:cursor-pointer hover:underline"
                    >
                        {t_imprint}
                    </a>
                </div>
            </article>
        </dialog>
    );
};
